/**
 * KILL TAPE — the offline kill-cam contract (Phase 1 tuning harness).
 *
 * A versioned, JSON-serializable snapshot of ONE real kill: the exact frozen window the scene director consumes
 * (`FrozenReplay` — transforms + combat events + environment, straight out of the REAL `ReplayBuffer`, NOT forked)
 * plus the perspective (`heroId`), the kill time, and a block of MEASURED features that label the tape (kill type,
 * engagement distance + bucket, mood, fatal ball id, scene density).
 *
 * This is the interface the offline bench (`tools/killcam-bench`) loads to tune the camera against an acervo of
 * REAL kills with zero deploy, and the shape the client debug hook `window.__cc.dumpKill()` emits from a live tape.
 * It is PURE of THREE / DOM — it imports only the pure director/buffer modules + protocol, so a headless tsx tool
 * can load it. Round-trip tested in killtape.test.ts.
 *
 * WHY reuse `FrozenReplay` verbatim: the whole point of the harness is to feed the director the EXACT bytes it sees
 * in prod. A parallel/forked schema is how the previous synthetic demos diverged from real kills. So the tape is
 * "FrozenReplay + labels", nothing re-modelled.
 */
import { EventType } from '@piratas/protocol';
import type { FrozenReplay, RepEvent } from './replaybuffer.js';
import { moodFor, type Mood } from './shotdirector.js';

/** Bump on ANY change to the tape shape below (readers reject a mismatched major). */
export const TAPE_VERSION = 1;

/** Coarse engagement-distance buckets (metres) — the primary feature axis the director will branch on in Phase 2. */
export type DistBucket = 'point-blank' | 'close' | 'mid' | 'long';
/** Measured kill archetype (from the events on the tape, never guessed by the harness). */
export type KillKind = 'cannon' | 'ram' | 'flood';

/** Measured labels for one tape (all derived from the frozen window — the bench buckets/aggregates on these). */
export interface KillFeatures {
  kind: KillKind;
  distBucket: DistBucket;
  engageDist: number;      // killer→victim separation at the fatal moment (m); -1 if a party is off-tape
  fatalBallId: number;     // the SINK's stamped fatal ball id (v56); 0 = ram / flood / none
  night: number;           // 0..1 day→night at the kill moment (client-render state; see harness caveat)
  storm: number;           // 0..1 storm intensity at the kill moment (server-authoritative)
  windStrength: number;    // wind strength at the kill moment
  mood: Mood;              // moodFor(night, storm, wind) — the grade/music bucket the director would pick
  shipsInWindow: number;   // distinct ship ids seen across the window (melee density)
  ballsPeak: number;       // max cannonballs in flight in any single frame (broadside density)
  sinkCount: number;       // distinct ships that sank in the window (multi-kill signal)
}

/** Capture provenance (who/what produced the tape — never consumed by the director, pure bookkeeping). */
export interface KillMeta {
  capturedAtMs: number;
  source: string;          // e.g. 'killcam-bench/corpus' | 'client/__cc.dumpKill'
  scenario?: string;       // the corpus scenario label (e.g. 'huddle-close', 'spread-long', 'ram')
  victimId: number;
  killerId: number;
  victimCls: number;       // the doomed hull class (drives the wreck mesh in playback)
  note?: string;
}

/** The serialized kill. `frozen` is the verbatim `FrozenReplay`; everything else labels/frames it. */
export interface KillTape {
  version: number;         // TAPE_VERSION
  protocolVersion: number; // wire version the kill was captured under (56 = SINK.ballId reliable)
  heroId: number;          // the captain the director frames the cut around (the KILLER for a "you sank them" tape)
  killT: number;           // sim time of the fatal SINK (the trigger)
  frozen: FrozenReplay;    // the REAL frozen window (frames + events + t0/t1/killT) — unforked
  features: KillFeatures;
  meta: KillMeta;
}

/* --------------------------------- feature extraction (pure) --------------------------------- */

/** The fatal SINK nearest the trigger — the same selection the director's `fatalBlow` makes. */
function fatalSink(frozen: FrozenReplay): RepEvent | null {
  let best: RepEvent | null = null, bestD = Infinity;
  for (const e of frozen.events) {
    if (e.kind !== EventType.SINK) continue;
    const d = Math.abs(e.t - frozen.killT);
    if (d < bestD) { bestD = d; best = e; }
  }
  return best;
}

/** Was there a ram impact within ~1.4 s of the fatal moment (the director's ram-kill gate)? */
function ramNear(frozen: FrozenReplay, t: number): boolean {
  for (const e of frozen.events) if (e.kind === EventType.RAM && Math.abs(e.t - t) < 1.4) return true;
  return false;
}

/** A ship's (x, y) at the frame nearest sim time `t` (searches self + ships), or null if never present. */
function shipPosNear(frozen: FrozenReplay, id: number, t: number): { x: number; y: number } | null {
  let best: { x: number; y: number } | null = null, bestD = Infinity;
  for (const fr of frozen.frames) {
    const d = Math.abs(fr.t - t);
    if (d >= bestD) continue;
    if (fr.self && fr.self.id === id) { best = { x: fr.self.x, y: fr.self.y }; bestD = d; continue; }
    for (const s of fr.ships) if (s.id === id) { best = { x: s.x, y: s.y }; bestD = d; break; }
  }
  return best;
}

/** The doomed hull's last-known class (for the wreck mesh); 1 (brigantine) if never seen. */
function classOf(frozen: FrozenReplay, id: number): number {
  for (let i = frozen.frames.length - 1; i >= 0; i--) {
    const f = frozen.frames[i]!;
    if (f.self && f.self.id === id) return f.self.cls;
    for (const s of f.ships) if (s.id === id) return s.cls;
  }
  return 1;
}

export function distBucketOf(dist: number): DistBucket {
  if (dist < 0) return 'mid';               // unknown separation → neutral bucket
  if (dist < 25) return 'point-blank';
  if (dist < 70) return 'close';
  if (dist < 140) return 'mid';
  return 'long';
}

/** Environment (night/storm/wind) at the frame nearest the fatal moment. */
function envNear(frozen: FrozenReplay, t: number): { night: number; storm: number; windStrength: number } {
  let night = 0, storm = 0, windStrength = 1, bestD = Infinity;
  for (const fr of frozen.frames) {
    const d = Math.abs(fr.t - t);
    if (d < bestD) { bestD = d; night = fr.env.night; storm = fr.env.storm; windStrength = fr.env.windStrength; }
  }
  return { night, storm, windStrength };
}

/** Measure the labels for a frozen window. Pure; identical inputs → identical features. */
export function extractFeatures(frozen: FrozenReplay): KillFeatures {
  const sink = fatalSink(frozen);
  const victimId = sink ? sink.a : 0;
  const killerId = sink ? sink.b : 0;
  const fatalBallId = sink ? (sink.c | 0) : 0;
  const t = sink ? sink.t : frozen.killT;

  let kind: KillKind;
  if (fatalBallId && killerId) kind = 'cannon';
  else if (ramNear(frozen, t)) kind = 'ram';
  else kind = 'flood';

  const kp = killerId ? shipPosNear(frozen, killerId, t) : null;
  const vp = victimId ? shipPosNear(frozen, victimId, t) : (sink ? { x: sink.x, y: sink.y } : null);
  const engageDist = kp && vp ? Math.hypot(kp.x - vp.x, kp.y - vp.y) : -1;

  const env = envNear(frozen, t);
  const ships = new Set<number>();
  const sinks = new Set<number>();
  let ballsPeak = 0;
  for (const fr of frozen.frames) {
    if (fr.self) ships.add(fr.self.id);
    for (const s of fr.ships) ships.add(s.id);
    if (fr.balls.length > ballsPeak) ballsPeak = fr.balls.length;
  }
  for (const e of frozen.events) if (e.kind === EventType.SINK) sinks.add(e.a);

  return {
    kind,
    distBucket: distBucketOf(engageDist),
    engageDist: engageDist < 0 ? -1 : +engageDist.toFixed(1),
    fatalBallId,
    night: +env.night.toFixed(3),
    storm: +env.storm.toFixed(3),
    windStrength: +env.windStrength.toFixed(3),
    mood: moodFor(env.night, env.storm, env.windStrength),
    shipsInWindow: ships.size,
    ballsPeak,
    sinkCount: sinks.size,
  };
}

/* --------------------------------- build / (de)serialize --------------------------------- */

/**
 * Build a tape from a frozen window. `heroId` is the perspective the cut is framed around — for a corpus tape that
 * is the KILLER (a "you sank them" highlight). Extra meta (source/scenario/note) is merged over the derived meta.
 */
export function buildTape(frozen: FrozenReplay, heroId: number, protocolVersion: number, meta: Partial<KillMeta> = {}): KillTape {
  const sink = fatalSink(frozen);
  const victimId = sink ? sink.a : 0;
  const killerId = sink ? sink.b : 0;
  return {
    version: TAPE_VERSION,
    protocolVersion,
    heroId,
    killT: frozen.killT,
    frozen,
    features: extractFeatures(frozen),
    meta: {
      capturedAtMs: Date.now(),
      source: 'unknown',
      victimId,
      killerId,
      victimCls: classOf(frozen, victimId),
      ...meta,
    },
  };
}

export function serializeTape(tape: KillTape): string {
  return JSON.stringify(tape);
}

/** Parse + validate a tape JSON. Throws a descriptive Error on a shape/version mismatch (fail loud in the bench). */
export function deserializeTape(json: string): KillTape {
  let obj: unknown;
  try { obj = JSON.parse(json); } catch (e) { throw new Error(`killtape: invalid JSON (${(e as Error).message})`); }
  return validateTape(obj);
}

/** Validate a parsed object is a well-formed tape of a compatible version. Returns it typed, or throws. */
export function validateTape(obj: unknown): KillTape {
  if (!obj || typeof obj !== 'object') throw new Error('killtape: not an object');
  const t = obj as Partial<KillTape>;
  if (typeof t.version !== 'number') throw new Error('killtape: missing version');
  if (t.version !== TAPE_VERSION) throw new Error(`killtape: version ${t.version} != reader ${TAPE_VERSION}`);
  if (typeof t.heroId !== 'number') throw new Error('killtape: missing heroId');
  if (typeof t.killT !== 'number') throw new Error('killtape: missing killT');
  const f = t.frozen as FrozenReplay | undefined;
  if (!f || !Array.isArray(f.frames) || !Array.isArray(f.events)) throw new Error('killtape: missing frozen.frames/events');
  if (f.frames.length < 2) throw new Error(`killtape: frozen has ${f.frames.length} frames (< 2 — unusable)`);
  if (typeof f.t0 !== 'number' || typeof f.t1 !== 'number' || typeof f.killT !== 'number') throw new Error('killtape: frozen missing t0/t1/killT');
  if (!t.features || typeof (t.features as KillFeatures).kind !== 'string') throw new Error('killtape: missing features');
  if (!t.meta || typeof (t.meta as KillMeta).source !== 'string') throw new Error('killtape: missing meta');
  return t as KillTape;
}
